// Real-money Gem purchases. The client only ever names a SKU: prices live on the Worker.
//
// Flow: POST /v1/orders {sku} -> full-page redirect to the payment page -> the gateway's signed
// webhook (or a reconcile) makes the Worker write users/{uid}/tlPendingGrants/{orderId} ->
// this client credits it, saves, uploads, and only then acknowledges it (POST .../applied).
// Credit is idempotent per order id (GameState.purchases), so a replayed grant never pays twice
// and an unacknowledged one is simply acknowledged next time.
import { collection, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore';
import { customGems } from '../data/gemPacks';
import type { Game } from '../game/game';
import { api } from './api';
import type { Accounts } from './auth';
import type { CloudSave } from './cloudSave';
import { GRANTS_COLLECTION } from './config';

export interface CatalogItem {
  sku: string;
  title: string;
  priceMinor: number;
  currency: string;
  priceString: string;
  nominalAmount: number;
}

export type OrderState = 'CREATED' | 'CHECKOUT' | 'PENDING' | 'AUTHORIZED' | 'AWAITING_APPROVAL' | 'COMPLETED'
  | 'DECLINED' | 'CANCELLED' | 'FAILED' | 'REFUNDED' | string;

export interface PaymentEvents {
  /** Gems landed in the wallet. */
  credited(gems: number): void;
  /** Back from the payment page; the result is being confirmed. */
  returned(orderId: string): void;
  /** The payment on the page we came back from didn't go through. */
  notCompleted(state: OrderState): void;
}

/**
 * Gems a SKU pays out: "gems_<amount>" for fixed packs, "gems_custom_<eur>" for the custom amount
 * (src/data/gemPacks.ts, mirrored by the Worker); null for anything this build can't credit.
 */
export function gemsForSku(sku: string): number | null {
  const key = sku.split('.')[0] ?? '';
  const custom = /^gems_custom_([1-9]\d{0,3})$/.exec(key);
  if (custom) return customGems(Number(custom[1]));
  const m = /^gems_(\d+)$/.exec(key);
  return m ? Number(m[1]) : null;
}

const ORDER_KEY = 'tl.pay.order';
const FAILED_STATES = new Set(['DECLINED', 'CANCELLED', 'FAILED']);

export class Payments {
  catalog: CatalogItem[] = [];
  private stopGrants: Unsubscribe | null = null;
  /** Grants being worked on right now: Firestore replays snapshots, and one grant must not run twice at once. */
  private inFlight = new Set<string>();

  constructor(
    private accounts: Accounts,
    private game: Game,
    private cloud: CloudSave,
    private saveLocal: () => void,
    private on: PaymentEvents,
  ) {}

  async loadCatalog(): Promise<CatalogItem[]> {
    const r = await api<{ items: CatalogItem[] }>('/v1/catalog');
    this.catalog = (r.items ?? []).filter((i) => gemsForSku(i.sku) !== null).sort((a, b) => a.priceMinor - b.priceMinor);
    return this.catalog;
  }

  /** Creates the order and leaves for the payment page. Throws ApiError (e.g. spend_limit) on refusal. */
  async buy(sku: string): Promise<void> {
    const r = await api<{ orderId: string; redirectUrl?: string }>('/v1/orders', { body: { sku }, token: this.accounts.token });
    if (!r.redirectUrl) throw new Error('The payment page could not be opened. Please try again.');
    try { sessionStorage.setItem(ORDER_KEY, r.orderId); } catch { /* storage blocked: ?order= still brings it back */ }
    window.location.href = r.redirectUrl;
  }

  /** The order we just came back from (the Worker returns players to ?order=<id>), removed from the URL. */
  consumeReturnOrder(): string | null {
    const url = new URL(window.location.href);
    let id = url.searchParams.get('order');
    if (id) {
      url.searchParams.delete('order');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
    try {
      id = id ?? sessionStorage.getItem(ORDER_KEY);
      sessionStorage.removeItem(ORDER_KEY);
    } catch { /* storage blocked */ }
    return id;
  }

  /** Asks the Worker to check the order with the gateway now, in case its webhook is slow. */
  async confirmReturn(orderId: string): Promise<void> {
    this.on.returned(orderId);
    try {
      const r = await api<{ state: OrderState; granted?: boolean }>(`/v1/orders/${encodeURIComponent(orderId)}/reconcile`,
        { method: 'POST', body: {}, token: this.accounts.token });
      if (FAILED_STATES.has(r.state)) this.on.notCompleted(r.state);
    } catch (e) {
      console.warn('Order check failed (the grant still arrives by webhook):', e);
    }
  }

  /** Listens for this player's unapplied grants for as long as they're signed in. */
  watchGrants(): void {
    this.stopWatching();
    const uid = this.accounts.user?.uid;
    if (!uid) return;
    const q = query(collection(this.accounts.db, 'users', uid, GRANTS_COLLECTION), where('applied', '==', false));
    this.stopGrants = onSnapshot(q, (snap) => {
      snap.forEach((d) => void this.applyGrant(d.id, String(d.data().sku ?? '')));
    }, (e) => console.warn('Grant listener error:', e));
  }

  stopWatching(): void { this.stopGrants?.(); this.stopGrants = null; }

  private async applyGrant(orderId: string, sku: string): Promise<void> {
    if (this.inFlight.has(orderId)) return;
    const gems = gemsForSku(sku);
    // Fail closed: a SKU this build can't credit stays pending for a build that can.
    if (gems === null) { console.warn(`Grant ${orderId}: unknown SKU ${sku}, left pending`); return; }
    this.inFlight.add(orderId);
    try {
      if (this.game.creditPurchase(orderId, gems)) this.on.credited(gems);
      this.saveLocal();
      // Acknowledge only once the account's cloud save holds the credit, so signing in elsewhere
      // can't load a save without it after the server stopped re-sending the grant.
      if (!(await this.cloud.flush())) return;
      await api(`/v1/orders/${encodeURIComponent(orderId)}/applied`, { method: 'POST', body: {}, token: this.accounts.token });
    } catch (e) {
      console.warn(`Grant ${orderId} not acknowledged yet:`, e);
    } finally {
      this.inFlight.delete(orderId);
    }
  }
}
